import { Eye, Edit2, Trash2, Check, Building2 } from "lucide-react";
import { useState, useEffect } from "react";
import { SearchBar, StatusBadge, EmptyState } from "../../index.jsx";
import { filterUsers } from "../../utils/filter.js";
import { userService } from "../../../../services/admin/userService.js";
import ConfirmDeleteModal from "./ConfirmDeleteModal.jsx";
import Pagination from "../../../common/Pagination.jsx";

export default function UsersTable({
  users,
  searchQuery,
  setSearchQuery,
  onSelect,
  setIsUserOnboarding,
  onBack
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const filtered = filterUsers(users, searchQuery);
  
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);
  const handleSelect = async (id, for_delete = false, for_edit = false) => {
    const user = await userService.getById(id);
    if (for_delete) {
      setUser(user.data);
      return;
    }
    if (for_edit) {
      setUser(user.data);
      setIsUserOnboarding(true);
    }
    onSelect(user.data);
  };

  const handleDeleteConfirm = async () => {
    try {
      await userService.delete(user.id);
      setShowDeleteModal(false);
      setShowToast(true);
      onBack?.();
      setTimeout(() => {
        setShowToast(false);
      }, 1500);
    } catch (error) {
      console.error("Error revoking user:", error);
      alert("Failed to revoke user. Please try again.");
    }
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-auto max-h-[calc(100vh-140px)]">
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
          User Directory
        </h3>
        
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
            itemsPerPage={itemsPerPage} 
            onItemsPerPageChange={setItemsPerPage} 
          />
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search users, email, or company…"
          />
        </div>
      </div>

      <div className="w-full flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                User Name
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Phone Number
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Company Assigned
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Role
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Status
              </th>
              <th className="px-6 py-4 font-medium text-center whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedData.length > 0 ? (
              paginatedData.map((u) => (
                <tr
                  key={u.id}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1d57a5]/10 text-[#1d57a5] dark:bg-[#4a8ce3]/20 dark:text-[#4a8ce3] flex items-center justify-center font-bold text-sm">
                        {u.firstName.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 dark:text-slate-200 whitespace-nowrap group-hover:text-[#1d57a5] dark:group-hover:text-[#4a8ce3] transition-colors">
                          {u.firstName + " " + u.middleName + " " + u.lastName}
                        </div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 whitespace-nowrap">
                          {u.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-400 text-sm whitespace-nowrap">
                    {u.mobileNo}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-slate-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {u.companyName ? u.companyName : u.company ? u.company : "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center justify-center px-3 py-1 rounded-md text-[11px] font-bold tracking-wide ${
                        u.role.toLowerCase() !== "user"
                          ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wide ${
                        u.status === "ACTIVE" || !u.status
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {u.status || "ACTIVE"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
                        <button
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="View"
                          onClick={() => handleSelect(u.id)}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="Edit"
                          onClick={() => handleSelect(u.id, false, true)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          title="Delete"
                          onClick={() => {
                            handleSelect(u.id, true);
                            setShowDeleteModal(true);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <EmptyState query={searchQuery} label="users" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {
        user && (
          <ConfirmDeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            userName={`${user.firstName} ${user.lastName}`}
          />  
        )
      }

      {showToast && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className="w-7 h-7 rounded-full bg-[#36a852] flex items-center justify-center flex-shrink-0">
            <Check size={14} strokeWidth={3} />
          </div>
          <span className="text-sm font-semibold">User revoked successfully!</span>
        </div>
      )}
    </div>
  );
}
